import type { FaqAdminCategory, FaqSearchMiss } from '@shared/schemas';

export type LoadStatus = 'idle' | 'loading' | 'loaded' | 'failed';

/**
 * How one write ended, under the request id it was dispatched with. Kept in a
 * list until the page acknowledges it: the queue starts the next write in the
 * same tick, so a single status would be overwritten before the page saw it,
 * and a background write's success could close a dialog whose own write is
 * still queued.
 */
export interface MutationOutcome {
  requestId: string;
  status: 'succeeded' | 'failed';
  /** The message to show when it failed. */
  error: string | null;
}

/** What the Undo of the last deletion would bring back. */
export interface DeletedItem {
  kind: 'category' | 'question';
  id: string;
  /** Category name or question text, for the snackbar. */
  label: string;
}

/**
 * Redux state for the admin FAQ page (#735).
 *
 * `categories` is the whole tree from `GET /admin/faq`, in display order, hidden
 * categories and drafts included. Moves are applied optimistically and the tree
 * is reloaded when the server refuses one. `lastDeleted` backs the Undo snackbar.
 * Ephemeral UI (open dialog, open menu) stays local to the page.
 */
export interface AdminFaqState {
  categories: FaqAdminCategory[];
  loadStatus: LoadStatus;
  /** The write the queue is running now, if any. */
  pendingRequestId: string | null;
  /** Writes that ended and that the page has not acknowledged yet, oldest first. */
  outcomes: MutationOutcome[];
  lastDeleted: DeletedItem | null;
  /**
   * Client searches that found nothing (#735), most searched first. A dismissed
   * one leaves `items` at once, before the server answers.
   */
  searchMisses: { items: FaqSearchMiss[]; status: LoadStatus };
  /**
   * The last dismiss the server took, with the stamp its Undo hands back, or the
   * failure to tell; the page toasts it, then clears it.
   */
  searchMissNotice: SearchMissNotice | null;
}

/** Where a search sat in the list, to put it back there. */
export interface PlacedSearchMiss {
  item: FaqSearchMiss;
  index: number;
}

export type SearchMissNotice =
  | ({ kind: 'dismissed'; dismissedAt: string } & PlacedSearchMiss)
  | { kind: 'failed'; message: string };

export const initialState: AdminFaqState = {
  categories: [],
  loadStatus: 'idle',
  pendingRequestId: null,
  outcomes: [],
  lastDeleted: null,
  searchMisses: { items: [], status: 'idle' },
  searchMissNotice: null,
};
